import React, { useEffect, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeftIcon } from '@heroicons/react/24/outline';
import AdminLayout from '../AdminLayout';
import { supabase } from '../../context/AuthContext';
import { convertToMelbourneDateAndTime } from '../../utils/dateUtils';

const ChevronIcon = ({ open }) => (
  open ? (
    <svg className="w-5 h-5 inline-block" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 15l-7-7-7 7" />
    </svg>
  ) : (
    <svg className="w-5 h-5 inline-block" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
    </svg>
  )
);

const getArchivedSevsStatusBadgeClassName = (status) => {
  switch (status) {
    case 'Cant be renewed':
      return 'bg-red-100 text-red-700';
    case 'Done':
    default:
      return 'bg-emerald-100 text-emerald-700';
  }
};

const getArchivedSevsActionClassName = (reason) => {
  if (reason) {
    return 'bg-red-50 text-red-700 ring-red-100';
  }

  return 'bg-blue-50 text-blue-700 ring-blue-100';
};



const ArchivedSevs = () => {
  const navigate = useNavigate();
  const [archivedData, setArchivedData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [openActivityIdx, setOpenActivityIdx] = useState(null);
  const [activeFilter, setActiveFilter] = useState('Total');
  const [sortOrder, setSortOrder] = useState('desc');

  const parseSevsExpiryDate = (value) => {
    if (!value) return Number.NEGATIVE_INFINITY;

    const parts = value.split('/').map(Number);
    if (parts.length === 3 && parts.every(part => !Number.isNaN(part))) {
      const [day, month, year] = parts;
      return new Date(year, month - 1, day).getTime();
    }

    const parsed = new Date(value).getTime();
    return Number.isNaN(parsed) ? Number.NEGATIVE_INFINITY : parsed;
  };

  useEffect(() => {
    const fetchArchivedData = async () => {
      setLoading(true);
      setError(null);
      try {
        const { data, error } = await supabase
          .from('sevs_expiry_management')
          .select(`
            *,
            customer_model_reports!inner(
              id,
              make,
              model,
              chassis_code,
              sevs_entry,
              sevs_expiry,
              mr_approval
            )
          `)
          .in('status', ['Done', 'Cant be renewed'])
          .order('created_at', { ascending: false });

        if (error) throw error;

        const transformedData = data.map(item => ({
          id: item.id,
          mr_id: item.mr_id,
          status: item.status,
          sevs_activity_logs: item.sevs_activity_logs,
          created_at: item.created_at,
          updated_at: item.updated_at,
          sevs_expiry: item.sevs_expiry || item.customer_model_reports.sevs_expiry,
          sevs_approval_no: item.sevs_approval_no,
          mr_wi_reference: item.mr_wi_reference,
          mr_vc_reference: item.mr_vc_reference,
          make: item.customer_model_reports.make,
          model: item.customer_model_reports.model,
          chassis_code: item.customer_model_reports.chassis_code,
          sevs_entry: item.customer_model_reports.sevs_entry,
          mr_approval: item.customer_model_reports.mr_approval
        }));
        
        setArchivedData(transformedData);
      } catch (err) {
        setError(err.message);
        setArchivedData([]);
      }
      setLoading(false);
    };
    fetchArchivedData();
  }, []);

  // Compute counts for each status
  const counts = useMemo(() => {
    return {
      'Total': archivedData.length,
      'Done': archivedData.filter(d => d.status === 'Done').length,
      'Cant be renewed': archivedData.filter(d => d.status === 'Cant be renewed').length,
    };
  }, [archivedData]);

  // Filtered data based on active filter
  const filteredAndSortedData = useMemo(() => {
    if (activeFilter === 'Total') return archivedData;
    return archivedData.filter(d => d.status === activeFilter);
  }, [archivedData, activeFilter]);

  const displayedData = useMemo(() => {
    const sortedData = [...filteredAndSortedData].sort((a, b) => {
      const aDate = parseSevsExpiryDate(a.sevs_expiry);
      const bDate = parseSevsExpiryDate(b.sevs_expiry);
      return sortOrder === 'desc' ? bDate - aDate : aDate - bDate;
    });

    return sortedData;
  }, [filteredAndSortedData, sortOrder]);

  const parseActivityLog = (logObj) => {
    if (!logObj || typeof logObj !== 'object') return [];
    return Object.entries(logObj)
      .flatMap(([timestamp, users]) =>
        Object.values(users).map(user => ({
          date: timestamp,
          user: user.name,
          email: user.email,
          action: user.status,
          reason: user.reason
        }))
      )
      .sort((a, b) => new Date(b.date) - new Date(a.date));
  };


  return (
    <AdminLayout
      activeItemId="archived-sevs"
      title="Archived SEVS"
      subtitle="Review completed SEVS records and inspect their activity history."
      contentClassName="w-full max-w-none"
      footerContainerClassName="mt-10 lg:mt-14"
    >
      <div className="space-y-6">
        <div className="rounded-[2rem] border border-slate-200 bg-white p-6 shadow-sm sm:p-7">
          <div className="flex flex-col gap-3 border-b border-slate-200 pb-5 sm:flex-row sm:items-end sm:justify-between">
            <div>
              <p className="text-md font-medium uppercase tracking-[0.04em]">Archived SEVS Overview</p>
              <p className="mt-1 text-sm text-slate-500">Select status to filter the records below.</p>
            </div>
          </div>

          <div className="mt-6 hidden gap-4 sm:grid sm:grid-cols-3">
            <button
              onClick={() => setActiveFilter('Total')}
              className={`rounded-[1.75rem] border p-5 text-left shadow-sm transition-all duration-300 ease-out ${activeFilter === 'Total' ? 'border-blue-700 bg-blue-600 text-white shadow-lg shadow-blue-200/60 ring-1 ring-blue-200/70 scale-[1.01]' : 'border-blue-200 bg-white hover:border-blue-300 hover:shadow-md hover:-translate-y-0.5'}`}
            >
              <p className={`text-sm font-semibold tracking-wide ${activeFilter === 'Total' ? 'text-white' : 'text-slate-500'}`}>Total</p>
              <p className={`mt-3 text-2xl font-semibold leading-none ${activeFilter === 'Total' ? 'text-white' : 'text-slate-900'}`}>{counts['Total']}</p>
            </button>
            <button
              onClick={() => setActiveFilter('Done')}
              className={`rounded-[1.75rem] border p-5 text-left shadow-sm transition-all duration-300 ease-out ${activeFilter === 'Done' ? 'border-emerald-300 bg-emerald-100 text-emerald-900 shadow-lg shadow-emerald-100/80 ring-1 ring-emerald-200/80 scale-[1.01]' : 'border-emerald-200 bg-white hover:border-emerald-300 hover:bg-emerald-50/70 hover:shadow-md hover:-translate-y-0.5'}`}
            >
              <p className={`text-sm font-semibold tracking-wide ${activeFilter === 'Done' ? 'text-emerald-900' : 'text-slate-500'}`}>Done</p>
              <p className={`mt-3 text-2xl font-semibold leading-none ${activeFilter === 'Done' ? 'text-emerald-900' : 'text-emerald-600'}`}>{counts['Done']}</p>
            </button>
            <button
              onClick={() => setActiveFilter('Cant be renewed')}
              className={`rounded-[1.75rem] border p-5 text-left shadow-sm transition-all duration-300 ease-out ${activeFilter === 'Cant be renewed' ? 'border-red-300 bg-red-100 text-red-900 shadow-lg shadow-red-100/80 ring-1 ring-red-200/80 scale-[1.01]' : 'border-red-200 bg-white hover:border-red-300 hover:bg-red-50/70 hover:shadow-md hover:-translate-y-0.5'}`}
            >
              <p className={`text-sm font-semibold tracking-wide ${activeFilter === 'Cant be renewed' ? 'text-red-900' : 'text-slate-500'}`}>Can't be renewed</p>
              <p className={`mt-3 text-2xl font-semibold leading-none ${activeFilter === 'Cant be renewed' ? 'text-red-900' : 'text-red-600'}`}>{counts['Cant be renewed']}</p>
            </button>
          </div>

          <div className="mt-4 sm:hidden">
            <select
              className="w-full rounded-2xl border border-slate-300 bg-white px-4 py-3 text-sm text-slate-900 shadow-sm transition focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-100"
              value={activeFilter}
              onChange={(event) => setActiveFilter(event.target.value)}
            >
              <option value="Total">All Statuses</option>
              <option value="Done">Done</option>
              <option value="Cant be renewed">Can't be renewed</option>
            </select>

            <div className="relative mt-4 inline-flex w-full items-center">
              <select
                aria-label="SEVS expiry order"
                value={sortOrder}
                onChange={(event) => {
                  setSortOrder(event.target.value);
                  setOpenActivityIdx(null);
                }}
                className="w-full appearance-none rounded-2xl border border-slate-300 bg-white bg-none px-4 py-3 pr-10 text-sm text-slate-900 shadow-sm transition [-moz-appearance:none] [-webkit-appearance:none] focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-100"
              >
                <option value="desc">SEVS Expiry (desc)</option>
                <option value="asc">SEVS Expiry (asc)</option>
              </select>
              <div className="pointer-events-none absolute right-4 top-1/2 -translate-y-1/2 text-slate-400">
                <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </div>
            </div>
          </div>
        </div>

        <div className="rounded-[2rem] border border-slate-200 bg-white p-6 shadow-sm sm:p-7">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
            <h2 className="text-md font-medium text-slate-900 uppercase tracking-[0.04em]">Archived SEVS Records</h2>
            <div className="relative hidden items-center self-start sm:inline-flex">
              <select
                aria-label="SEVS expiry order"
                value={sortOrder}
                onChange={(event) => {
                  setSortOrder(event.target.value);
                  setOpenActivityIdx(null);
                }}
                className="min-w-[190px] appearance-none rounded-xl border border-slate-200 bg-white py-2 pl-3 pr-9 text-sm font-medium text-slate-700 shadow-sm outline-none transition-colors duration-200 hover:border-slate-300 focus:border-slate-400 focus:ring-2 focus:ring-slate-200"
              >
                <option value="desc">SEVS Expiry (desc)</option>
                <option value="asc">SEVS Expiry (asc)</option>
              </select>
              <div className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-slate-400">
                <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </div>
            </div>
          </div>
          <div className="mt-6 overflow-hidden rounded-[1.75rem] border border-slate-200 bg-white shadow-sm shadow-slate-200/60">
            <div className="overflow-x-auto">
            <table className="min-w-full table-fixed bg-white">
              <thead className="bg-slate-50/80">
                <tr>
                  <th className="w-12"></th>
                  <th className="hidden w-[14%] px-4 py-4 text-left text-xs font-medium uppercase tracking-wider text-slate-500 sm:table-cell xl:w-[12%]">S. No</th>
                  <th className="w-[38%] px-2 py-3 text-left text-xs font-medium uppercase tracking-wider text-slate-500 sm:w-[29%] sm:px-4 sm:py-4 xl:w-[14%]">Car Details</th>
                  <th className="w-[38%] px-2 py-3 text-left text-xs font-medium uppercase tracking-wider text-slate-500 sm:w-[29%] sm:px-4 sm:py-4 xl:w-[14%]">Chassis Code</th>
                  <th className="hidden w-[14%] px-4 py-4 text-left text-xs font-medium uppercase tracking-wider text-slate-500 xl:table-cell">SEVS Entry</th>
                  <th className="hidden w-[14%] px-4 py-4 text-left text-xs font-medium uppercase tracking-wider text-slate-500 xl:table-cell">SEVS Expiry</th>
                  <th className="hidden w-[14%] px-4 py-4 text-left text-xs font-medium uppercase tracking-wider text-slate-500 xl:table-cell">MRE Approval</th>
                  <th className="w-[24%] px-2 py-3 text-left text-xs font-medium uppercase tracking-wider text-slate-500 sm:w-[28%] sm:px-4 sm:py-4 xl:w-[14%]">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-200">
                {loading ? (
                  <tr>
                    <td colSpan={8} className="py-8 text-center text-slate-500">Loading...</td>
                  </tr>
                ) : error ? (
                  <tr>
                    <td colSpan={8} className="py-8 text-center text-red-500">{error}</td>
                  </tr>
                ) : displayedData.length === 0 ? (
                  <tr>
                    <td colSpan={8} className="py-8 text-center text-slate-500">No archived SEVS records found.</td>
                  </tr>
                ) : (
                  displayedData.map((row, idx) => (
                    <React.Fragment key={row.id}>
                      <tr>
                        <td
                          className="py-4 px-2 text-center align-middle cursor-pointer"
                          onClick={() => setOpenActivityIdx(openActivityIdx === idx ? null : idx)}
                        >
                          <button
                            aria-label={openActivityIdx === idx ? 'Hide Activity' : 'Show Activity'}
                            onClick={() => setOpenActivityIdx(openActivityIdx === idx ? null : idx)}
                            className="focus:outline-none"
                          >
                            <ChevronIcon open={openActivityIdx === idx} />
                          </button>
                        </td>
                        <td
                          className="hidden w-[14%] py-4 px-4 break-words whitespace-normal text-sm text-slate-900 cursor-pointer sm:table-cell lg:whitespace-nowrap xl:w-[12%]"
                          onClick={() => setOpenActivityIdx(openActivityIdx === idx ? null : idx)}
                        >
                          {idx + 1}
                        </td>
                        <td
                          className="w-[38%] px-2 py-3 break-words whitespace-normal text-xs text-slate-900 cursor-pointer sm:w-[29%] sm:px-4 sm:py-4 sm:text-sm lg:whitespace-nowrap xl:w-[14%]"
                          onClick={() => setOpenActivityIdx(openActivityIdx === idx ? null : idx)}
                        >
                          {row.make} {row.model}
                        </td>
                        <td
                          className="w-[38%] px-2 py-3 break-words whitespace-normal text-xs text-slate-900 cursor-pointer sm:w-[29%] sm:px-4 sm:py-4 sm:text-sm lg:whitespace-nowrap xl:w-[14%]"
                          onClick={() => setOpenActivityIdx(openActivityIdx === idx ? null : idx)}
                        >
                          {row.chassis_code}
                        </td>
                        <td
                          className="hidden w-[14%] py-4 px-4 whitespace-nowrap text-slate-900 cursor-pointer xl:table-cell"
                          onClick={() => setOpenActivityIdx(openActivityIdx === idx ? null : idx)}
                        >
                          {row.sevs_entry}
                        </td>
                        <td
                          className="hidden w-[14%] py-4 px-4 whitespace-nowrap text-slate-900 cursor-pointer xl:table-cell"
                          onClick={() => setOpenActivityIdx(openActivityIdx === idx ? null : idx)}
                        >
                          {row.sevs_expiry}
                        </td>
                        <td
                          className="hidden w-[14%] py-4 px-4 whitespace-nowrap text-slate-900 cursor-pointer xl:table-cell"
                          onClick={() => setOpenActivityIdx(openActivityIdx === idx ? null : idx)}
                        >
                          {row.mr_approval}
                        </td>
                        <td className="w-[24%] px-2 py-3 break-words whitespace-normal text-xs text-slate-900 sm:w-[28%] sm:px-4 sm:py-4 sm:text-sm lg:whitespace-nowrap xl:w-[14%]">
                          <span className={`rounded-full px-3 py-1 text-xs font-semibold ${getArchivedSevsStatusBadgeClassName(row.status)}`}>
                            {row.status}
                          </span>
                        </td>
                      </tr>
                      {openActivityIdx === idx && (
                        <tr>
                          <td colSpan={8} className="bg-slate-50 px-4 py-4">
                            <div className="overflow-hidden rounded-[1.5rem] border border-slate-200 bg-white shadow-sm">
                              <div className="border-b border-slate-200 xl:hidden">
                                <div className="flex flex-col gap-3 border-b border-slate-200 bg-slate-50/80 px-5 py-4 sm:flex-row sm:items-center sm:justify-between">
                                  <div className="flex items-center gap-3">
                                    <span className="flex h-9 w-9 items-center justify-center rounded-2xl bg-slate-900 text-white shadow-sm sm:h-11 sm:w-11">
                                      <svg className="h-4 w-4 sm:h-5 sm:w-5" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" d="M9 12h6m-6 4h6m2 5H7a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5.586a1 1 0 0 1 .707.293l5.414 5.414a1 1 0 0 1 .293.707V19a2 2 0 0 1-2 2Z" />
                                      </svg>
                                    </span>
                                    <div>
                                      <p className="text-sm font-medium uppercase tracking-[0.04em] xl:text-md">Details</p>
                                      <h4 className="text-xs font-medium text-slate-500 xl:text-sm">Review the SEVS record details.</h4>
                                    </div>
                                  </div>
                                </div>
                                <div className="px-5 py-4">
                                  <div className="grid gap-3 sm:grid-cols-3">
                                    <div className="min-w-0 rounded-2xl border border-slate-200 bg-slate-50/80 px-4 py-3">
                                      <p className="text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-400 xl:text-[11px]">SEVS Entry</p>
                                      <p className="mt-2 break-words text-xs font-normal text-slate-700 xl:text-sm">{row.sevs_entry || '-'}</p>
                                    </div>
                                    <div className="min-w-0 rounded-2xl border border-slate-200 bg-slate-50/80 px-4 py-3">
                                      <p className="text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-400 xl:text-[11px]">SEVS Expiry</p>
                                      <p className="mt-2 break-words text-xs font-normal text-slate-700 xl:text-sm">{row.sevs_expiry || '-'}</p>
                                    </div>
                                    <div className="min-w-0 rounded-2xl border border-slate-200 bg-slate-50/80 px-4 py-3">
                                      <p className="text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-400 xl:text-[11px]">MRE Approval</p>
                                      <p className="mt-2 break-words text-xs font-normal text-slate-700 xl:text-sm">{row.mr_approval || '-'}</p>
                                    </div>
                                  </div>
                                </div>
                              </div>

                              <div className="flex flex-col gap-3 border-b border-slate-200 bg-slate-50/80 px-5 py-4 sm:flex-row sm:items-center sm:justify-between">
                                <div className="flex items-center gap-3">
                                  <span className="flex h-11 w-11 items-center justify-center rounded-2xl bg-slate-900 text-white shadow-sm">
                                    <svg className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
                                      <circle cx="4" cy="12" r="1.5" />
                                      <circle cx="4" cy="6" r="1.5" />
                                      <circle cx="4" cy="18" r="1.5" />
                                      <line x1="8" y1="6" x2="20" y2="6" strokeLinecap="round" />
                                      <line x1="8" y1="12" x2="20" y2="12" strokeLinecap="round" />
                                      <line x1="8" y1="18" x2="20" y2="18" strokeLinecap="round" />
                                    </svg>
                                  </span>
                                  <div>
                                    <p className="text-md font-medium uppercase tracking-[0.04em]">Activity Logs</p>
                                    <h4 className="text-sm font-medium text-slate-500">View email notification history.</h4>
                                  </div>
                                </div>
                                <span className="inline-flex items-center rounded-full bg-white px-3 py-1 text-xs font-semibold text-slate-500 ring-1 ring-slate-200 uppercase tracking-[0.04em]">
                                  {parseActivityLog(row.sevs_activity_logs).length} entries
                                </span>
                              </div>

                              {parseActivityLog(row.sevs_activity_logs).length === 0 ? (
                                <div className="px-5 py-8 text-center text-sm text-slate-400">No activity found.</div>
                              ) : (
                                <div>
                                  <div className="hidden border-b border-slate-200 bg-slate-50 px-5 py-3 lg:grid lg:grid-cols-[minmax(0,1.1fr)_minmax(0,1fr)_minmax(0,1.2fr)] lg:gap-4">
                                    <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">Date</p>
                                    <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">Email</p>
                                    <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">Action</p>
                                  </div>
                                  <div className="divide-y divide-slate-200">
                                  {parseActivityLog(row.sevs_activity_logs).map((log, i) => (
                                    <div
                                      key={i}
                                      className="grid gap-4 px-5 py-4 lg:grid-cols-[minmax(0,1.1fr)_minmax(0,1fr)_minmax(0,1.2fr)]"
                                    >
                                      <div className="min-w-0">
                                        <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-400 lg:hidden">Date</p>
                                        <p className="mt-2 text-sm font-normal text-slate-600 lg:mt-0">{convertToMelbourneDateAndTime(log.date)}</p>
                                      </div>
                                      <div className="min-w-0">
                                        <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-400 lg:hidden">Email</p>
                                        <p className="mt-2 break-all text-sm font-normal text-slate-600 lg:mt-0">{log.email}</p>
                                      </div>
                                      <div className="min-w-0">
                                        <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-400 lg:hidden">Action</p>
                                        <div className="mt-2 flex flex-wrap items-start gap-2 lg:mt-0">
                                          <div className={`inline-flex max-w-full flex-col overflow-hidden rounded-2xl px-3 py-2 text-xs font-semibold ring-1 ${getArchivedSevsActionClassName(log.reason)}`}>
                                            <span>{log.action}</span>
                                            {log.reason && (
                                              <div className="mt-2 border-t border-current/15 pt-2 text-xs font-normal leading-relaxed">
                                                <span className="font-semibold">Reason:</span>{' '}
                                                <span className="normal-case tracking-normal">{log.reason}</span>
                                              </div>
                                            )}
                                          </div>
                                        </div>
                                      </div>
                                    </div>
                                  ))}
                                  </div>
                                </div>
                              )}
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  ))
                )}
              </tbody>
            </table>
            </div>
          </div>
        </div>
      </div>
    </AdminLayout>
  );
};

export default ArchivedSevs; 
